/**
 * Toegang tot de API. Eén dunne laag.
 *
 * De typen die het domein beschrijven — onderdeel, maten, bevinding, stapsoort,
 * afdeling — komen uit `@houtstaat/engine`, zodat frontend en backend niet uit elkaar
 * kunnen lopen. Wat hier wél staat zijn de antwoordvormen van de endpoints: dat zijn
 * weergavemodellen met afgeleide velden (labels per stap, uren, varianten) en geen
 * domeinentiteiten.
 *
 * Wat de gebruiker niet mag zien, staat niet in het antwoord: `foutmeldingen` en
 * `uren` zijn `undefined` voor productie. Dat is geen UI-keuze maar de API die het
 * weglaat; de schermen hoeven er alleen op te controleren.
 */
import type {
  Afdeling,
  Bevinding,
  Bewerkingsregel,
  Coderingen,
  ConversieRegel,
  Machine,
  Maten,
  Normtijd,
  Onderdeel,
  PlaatMateriaal,
  Plaatsing,
  StapSoort,
  TomtomStap,
} from '@houtstaat/engine';

// ── Rollen en rechten ───────────────────────────────────────────────────────

export type Recht =
  | 'houtstaatBekijken'
  | 'werkvloerBekijken'
  | 'dashboardBekijken'
  | 'planningBekijken'
  | 'regelsBekijken'
  | 'modelBekijken'
  | 'beheerBekijken'
  | 'foutmeldingenZien'
  | 'rodeWaardenGoedkeuren'
  | 'urenZien'
  | 'normtijdenBewerken'
  | 'gegevensAanpassen'
  | 'dataInladen'
  | 'karrenSplitsen'
  | 'modelBeheren'
  | 'exporteren'
  | 'takenAfstrepen'
  | 'reworkStarten'
  | 'stapOpmerken'
  | 'stapInstellen'
  | 'volgordeOpenStappen'
  | 'volgordeAfgevinkteStappen'
  | 'producerenVrijgeven'
  | 'onHoldZetten'
  | 'planningWijzigen'
  | 'coderingenBewerken'
  | 'regelsBewerken'
  | 'gebruikersbeheer';

export type Rechten = Record<Recht, boolean>;

export interface Ik {
  naam: string;
  rollen: string[];
  rolLabel: string[];
  rechten: Rechten;
  matrix: {
    rollen: Array<{ sleutel: string; label: string }>;
    rechten: Array<{ sleutel: Recht; rollen: string[] }>;
  };
}

// ── Kleuren, boten ──────────────────────────────────────────────────────────

export interface Afdelingskleur {
  afdeling: Afdeling;
  kleur: string;
  tekst: string;
}

/** De kleur van een bewerkingskolom in de houtstaat — Excel RGB. */
export interface Bewerkingskleur {
  bewerking: string;
  kleur: string;
  tekst: string;
}

export interface Kleuren {
  afdelingen: Afdelingskleur[];
  /**
   * De kleuren van de bewerkingskolommen (FINEER groen, CNCZ geel, …). Dit is wat de
   * houtstaat kleurt. Bouwnummers hebben geen kleur: een bouwnummer is een nummer.
   */
  bewerkingen: Bewerkingskleur[];
  rework: { kleur: string; tekst: string; rand: string };
}

export interface Bouwnummer {
  bouwnummer: string;
  naam: string;
  omschrijving: string;
  versie: string;
  revisie: string;
  kleur: string;
  fotoVerplichtBij: StapSoort[];
}

// ── Bouwelement en kar ──────────────────────────────────────────────────────

/**
 * Status van een kar. De kar is de eenheid die door de werkplaats rijdt, dus de status
 * hoort daar en niet bij het bouwelement.
 */
export type Karstatus = 'concept' | 'produceren' | 'on-hold' | 'gereed';

export interface Handeling {
  door: string;
  op: string;
}

export interface OnHold extends Handeling {
  reden: string;
}

/** Begin en eind in de agenda, als `jjjj-mm-ddTuu:mm` zonder tijdzone; leeg als niet ingepland. */
export interface Planning {
  start?: string;
  eind?: string;
}

export interface StlBestand {
  verwijzing: string;
  /**
   * `stl` kan de viewer tekenen; `dwg` en `dxf` worden bewaard en zijn te downloaden,
   * want een browser krijgt ze niet in beeld zonder converter. Ontbreekt het veld, dan
   * is het een STL van vóór deze uitbreiding.
   */
  soort?: 'stl' | 'dwg' | 'dxf';
  bestandsnaam: string;
  bytes: number;
  geladenOp: string;
  geladenDoor: string;
  revisie?: string;
}

/**
 * Eén bouwelement: een samenstel dat op een kar staat. De voortgang zit op de kar —
 * die rijdt door de werkplaats met meerdere bouwelementen erop.
 */
export interface Bouwelement {
  id: string;
  bouwnummer: string;
  karId: string;
  plaatsing: Plaatsing;
  stl?: StlBestand;
  gewijzigdOp: string;
}

/** De plaatsing tot en met de kar; het bouwelement hangt daaronder. */
export type Karplaatsing = Omit<Plaatsing, 'bouwelement' | 'bouwelementNaam'>;

export type StapStatus = 'open' | 'bezig' | 'gereed';

export interface Afstreping {
  door: string;
  op: string;
  naRework?: string;
}

export interface Foto {
  verwijzing: string;
  bestandsnaam: string;
  door: string;
  op: string;
  onderdeelNummer?: string;
}

export interface Rework {
  id: string;
  aangevraagdDoor: string;
  aangevraagdOp: string;
  reden: string;
  vanafSleutel: string;
  oorspronkelijkeSleutel: string;
  aparteKar: boolean;
  /** Bij een aparte kar: de code van die kar binnen de ruimte, bv. `09R`. */
  karcode?: string;
  reworkKarId?: string;
  afgerondOp?: string;
  reworkMinuten?: number;
}

/** Eén stap van de kar zoals de schermen hem tonen. */
export interface Weergavestap {
  sleutel: string;
  soort: StapSoort;
  kolomnaam: string;
  afdeling: Afdeling;
  /** Kleur van de bewerkingskolom (Excel RGB). Afstrepen verandert deze kleur niet. */
  kleur: string;
  /** Leesbare tekstkleur op `kleur`. */
  tekstkleur: string;
  status: StapStatus;
  afstrepingen: Afstreping[];
  fotoVerplicht: boolean;
  normtijd: Normtijd;
  normZichtbaarVoorProductie: boolean;
  fotos: Foto[];
  opmerking?: string;
  verplaatst?: boolean;
  /** De waarde in het blok: die van het grootste onderdeel op de kar. */
  label: string;
  labelPerOnderdeel: Array<{ onderdeelNummer: string; label: string; toelichting?: string }>;
  /** Aantal verschillende waarden; > 1 betekent dat `label` er één van is. */
  varianten: number;
  toelichting?: string;
  normMinuten?: number;
  afgestreeptDoor?: string;
  afgestreeptOp?: string;
}

export interface Materiaalregel {
  id: string;
  /** Het bouwelement op de kar waar dit onderdeel bij hoort. */
  bouwelementId: string;
  onderdeel: Onderdeel;
  maten: Maten;
  tomtom: TomtomStap[];
  /** Velden met een openstaande rode waarde: die worden rood getoond. */
  rodeVelden: string[];
  /** Velden met overmaat karwei: die worden blauw getoond. */
  karweiVelden: string[];
}

export interface Foutmelding extends Bevinding {
  sleutel: string;
  onderdeelId: string;
  onderdeelNummer: string;
  goedgekeurd?: Handeling;
}

export interface Uren {
  gemaakt: number;
  resterend: number;
  rework: number;
  totaal: number;
}

export interface Kar {
  id: string;
  bouwnummer: string;
  nummer: string;
  /** De karcode binnen de ruimte, bv. `09`, met de naam van de kar. */
  kar: string;
  karNaam: string;
  plaatsing: Karplaatsing;
  status: Karstatus;
  onHold?: OnHold;
  planning: Planning;
  /** De bouwelementen op deze kar; de materiaalstaat is er per stuk op gegroepeerd. */
  bouwelementen: Array<{ id: string; code: string; naam: string; onderdelen: number }>;
  prioriteit: number;
  positie: number;
  route: Weergavestap[];
  huidigeStap: Weergavestap | null;
  onderdelen: number;
  stuks: number;
  voortgang: number;
  rework: Rework[];
  reworkActief: Rework | null;
  reworkVanKarId?: string;
  isRework: boolean;
  materiaalstaat: Materiaalregel[];
  /** Alleen gevuld als de rol foutmeldingen mag zien. */
  foutmeldingen?: Foutmelding[];
  /** Alleen gevuld als de rol uren mag zien. */
  uren?: Uren;
  gewijzigdOp: string;
}

// ── Navigatie ───────────────────────────────────────────────────────────────

/** Eén bouwelement in de boom, onder de kar waar het op staat. */
export interface NavBouwelement {
  id: string;
  code: string;
  naam: string;
  onderdelen: number;
  heeftStl: boolean;
}

export interface NavKar {
  id: string;
  kar: string;
  karNaam: string;
  status: Karstatus;
  onHold?: OnHold;
  planning: Planning;
  onderdelen: number;
  stuks: number;
  afdeling: Afdeling;
  stap: string;
  positie: number;
  stappen: number;
  rework: boolean;
  prioriteit: number;
  uren?: Uren;
  bouwelementen: NavBouwelement[];
}

/**
 * De boom: deck → sectie → ruimte → kar → bouwelement. Het deck is een naam en zit
 * niet in het nummer; de sectie is het tweede segment.
 */
export interface Navigatie {
  bouwnummer: Bouwnummer;
  decks: Array<{
    deck: string;
    secties: Array<{
      sectie: string;
      ruimtes: Array<{ ruimte: string; naam: string; karren: NavKar[] }>;
    }>;
  }>;
  tellingen: {
    karren: number;
    bouwelementen: number;
    karrenOpenOpWerkvloer: number;
    klaarVoorPlanning: number;
  };
}

// ── Antwoordvormen per scherm ───────────────────────────────────────────────

export interface KarAntwoord {
  kar: Kar;
  /** De bouwelementen die op deze kar staan. */
  bouwelementen: Bouwelement[];
  bouwnummer: Bouwnummer | null;
  vergrendeld: boolean;
  /** De andere karren in dezelfde ruimte, voor het tabblad "Karren". */
  karrenInRuimte: Array<{
    id: string;
    kar: string;
    karNaam: string;
    status: Karstatus;
    positie: number;
    stappen: number;
    afdeling: Afdeling;
    rework: boolean;
  }>;
}

export interface WerkvloerKar extends Kar {
  bouwnummer: string;
  bouwelementNaam: string;
}

export interface Werkvloer {
  bouwnummer: Bouwnummer;
  karren: WerkvloerKar[];
  filters: {
    totaal: number;
    afdelingen: Array<{ afdeling: Afdeling; karren: number }>;
    rework: number;
  };
  ververstOp: string;
}

export interface DashboardKar {
  id: string;
  onderdelen: number;
  stuks: number;
  afdeling: Afdeling;
  afdelingKleur: string;
  stap: string;
  voortgang: number;
  gereed: boolean;
  uren: Uren;
  reworks: number;
  reworkActief: Rework | null;
  prioriteit: number;
}

/** Eén regel op het dashboard: een kar, met de bouwelementen die erop staan. */
export interface DashboardKarrij {
  id: string;
  kar: string;
  karNaam: string;
  deck: string;
  sectie: string;
  ruimte: string;
  ruimtecode: string;
  status: Karstatus;
  onHold?: OnHold;
  planning: Planning;
  onderdelen: number;
  stuks: number;
  afdeling: string;
  stap: string;
  voortgang: number;
  gereed: boolean;
  uren: Uren;
  reworks: number;
  reworkActief: Rework | null;
  prioriteit: number;
  /** De route als strip: wat gereed is en waar de kar nu staat. */
  stappen: Array<{ sleutel: string; kolomnaam: string; kleur: string; gereed: boolean; nu: boolean }>;
  bouwelementen: Array<{ id: string; code: string; naam: string; onderdelen: number }>;
  verwachtGereed: string | null;
}

export interface Dashboard {
  bouwnummer: Bouwnummer;
  samenvatting: {
    gereedDeel: number;
    urenGemaakt: number;
    urenRework: number;
    urenResterend: number;
    karrenInRework: number;
    karrenOnHold: number;
    onHold: Array<{ id: string; naam: string; door?: string; op?: string; reden?: string }>;
    karren: number;
    /** Karren uit de begroting zonder onderdelen: die tellen hierboven niet mee. */
    karrenZonderHoutstaat: number;
    bouwelementen: number;
  };
  belasting: Array<{
    afdeling: Afdeling;
    kleur: string;
    karren: number;
    stuks: number;
    resterendeUren: number;
    dagcapaciteitUren: number;
    doorlooptijdDagen: number;
    bezettingsgraad: number;
  }>;
  karren: DashboardKarrij[];
  ververstOp: string;
}

/** Eén kar in de agenda van de planning, of in de lijst van wat nog in te plannen is. */
export interface AgendaKar {
  id: string;
  /** Van welk schip deze kar is; de agenda loopt over alle bouwnummers. */
  bouwnummer: string;
  kar: string;
  karNaam: string;
  sectie: string;
  ruimte: string;
  ruimtecode: string;
  status: Karstatus;
  onHold?: OnHold;
  start?: string;
  eind?: string;
  onderdelen: number;
  stuks: number;
  /** Resterende uren volgens de houtstaat; `null` zolang er geen onderdelen zijn. */
  urenHoutstaat: number | null;
  /** Begrote uren per afdeling uit MS Project. */
  begroot: Partial<Record<string, number>>;
  mspCode?: string;
  /** De lengte van een nieuwe afspraak, in werkuren. */
  urenAfspraak: number;
  inplanbaar: boolean;
  bezetting: Array<{ afdeling: Afdeling; kleur: string; uren: number; deel: number }>;
}

export interface PlanningAntwoord {
  /** De agenda's: één per bouwnummer, met zijn kleur. */
  bouwnummers: Bouwnummer[];
  karren: AgendaKar[];
  ververstOp: string;
}

export interface Gebeurtenis {
  soort: 'afstreping' | 'rework';
  op: string;
  door: string;
  stap?: string;
  afdeling?: Afdeling;
  kleur?: string;
  tekst: string;
}

// ── Bewerkingen en berekeningen (scherm 06) ─────────────────────────────────

export interface BlokkenBibliotheek {
  velden: ReadonlyArray<{ pad: string; label: string; eenheid: string }>;
  tabelwaarden: ReadonlyArray<{ pad: string; label: string; eenheid: string }>;
  kernparameters: ReadonlyArray<{ naam: string; uitleg: string; eenheid: string }>;
}

export interface RegelsAntwoord {
  regels: Bewerkingsregel[];
  afdelingen: readonly Afdeling[];
  soorten: readonly StapSoort[];
  blokken: BlokkenBibliotheek;
  /**
   * Karren waarvan de opgeslagen route niet meer klopt met de regels. De maten volgen
   * een regelwijziging meteen; de route van een kar die al onderweg is niet, want daar
   * hangen de afstrepingen aan.
   */
  afwijkendeKarren: string[];
}

export interface Regeluitkomst {
  regelId: string;
  naam: string;
  toegepast: boolean;
  actief: boolean;
  gezet: Array<{ parameter: string; waarde: string | number | boolean | null }>;
  label?: string;
  toelichting?: string;
}

export interface RegeltestAntwoord {
  onderdeel: {
    id: string;
    nummer: string;
    benaming: string;
    aantal: number;
    lengte: number;
    breedte: number;
    dikte: number;
    materiaal: string;
    zijden: Onderdeel['zijden'];
    kar: string;
  };
  uitkomsten: Regeluitkomst[];
  parameters: Record<string, string | number | boolean | null>;
  route: Array<{
    sleutel: string;
    kolomnaam: string;
    afdeling: Afdeling;
    kleur: string;
    label: string;
    toelichting?: string;
  }>;
  maten: Maten;
  keuze: Array<{ id: string; nummer: string; benaming: string; kar: string }>;
}

export interface ModelOnderdeel {
  id: string;
  nummer: string;
  benaming: string;
  aantal: number;
  lengte: number;
  breedte: number;
  dikte: number;
  materiaal: string;
  zijde0: string;
  zijde5: string;
  karId: string;
  kar: string;
  stl?: StlBestand;
}

export interface ModelAntwoord {
  bouwelement: {
    id: string;
    naam: string;
    ruimte: string;
    deck: string;
    stl?: StlBestand;
  };
  karren: Array<{ id: string; kar: string; karNaam: string }>;
  onderdelen: ModelOnderdeel[];
  mag: { uploaden: boolean };
}

/** De keuzemogelijkheden voor de inline menu's in de materiaalstaat. */
export interface Keuzes {
  materialen: string[];
  afwerkingen: string[];
  kanten: string[];
  diktes: Record<string, number[]>;
}

export interface AuditRegel {
  id: string;
  tijdstip: string;
  gebruiker: string;
  rol: string;
  actie: string;
  onderwerp: string;
  details?: string;
}

/** De velden die inline aanpasbaar zijn; de API weigert al het andere. */
export interface OnderdeelWijziging {
  aantal?: number;
  benaming?: string;
  lengte?: number;
  breedte?: number;
  dikte?: number;
  materiaal?: string;
  dekkend?: boolean;
  opmerking?: string;
  overmaatLengte?: number | '';
  overmaatBreedte?: number | '';
  overmaatKarweiLengte?: number | '';
  overmaatKarweiBreedte?: number | '';
  zijden?: Partial<Onderdeel['zijden']>;
}

// ── Verzoeklaag ─────────────────────────────────────────────────────────────

export class ApiFout extends Error {
  constructor(
    public readonly status: number,
    bericht: string,
    /** Bij een 403: welk recht ontbrak. */
    public readonly recht?: string,
  ) {
    super(bericht);
  }
}

async function verzoek<T>(pad: string, init?: RequestInit): Promise<T> {
  const antwoord = await fetch(`/api${pad}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (!antwoord.ok) {
    let bericht = `${antwoord.status} ${antwoord.statusText}`;
    let recht: string | undefined;
    try {
      const body = (await antwoord.json()) as { fout?: string; recht?: string };
      if (body.fout) bericht = body.fout;
      recht = body.recht;
    } catch {
      /* geen JSON-body */
    }
    throw new ApiFout(antwoord.status, bericht, recht);
  }
  return (await antwoord.json()) as T;
}

const post = <T>(pad: string, body?: unknown): Promise<T> =>
  verzoek<T>(pad, { method: 'POST', body: JSON.stringify(body ?? {}) });
const patch = <T>(pad: string, body: unknown): Promise<T> =>
  verzoek<T>(pad, { method: 'PATCH', body: JSON.stringify(body) });

export const api = {
  // Gebruiker, boten, kleuren
  ik: () => verzoek<Ik>('/me'),
  kleuren: () => verzoek<Kleuren>('/kleuren'),
  bouwnummers: () => verzoek<Bouwnummer[]>('/bouwnummers'),
  bouwnummerBijwerken: (nr: string, wijziging: Partial<Bouwnummer>) =>
    patch<Bouwnummer>(`/bouwnummers/${nr}`, wijziging),

  /**
   * Een goedkope stempel van "wanneer is er voor het laatst iets gewijzigd". De
   * schermen pollen dit en verversen zichzelf zodra hij verandert.
   */
  wijzigingen: (nr: string) => verzoek<{ stempel: string; op: string }>(`/wijzigingen/${nr}`),

  // Navigatie en kar
  navigatie: (nr: string) => verzoek<Navigatie>(`/navigatie/${nr}`),
  keuzes: (nr: string) => verzoek<Keuzes>(`/keuzes/${nr}`),
  kar: (nr: string, karId: string) => verzoek<KarAntwoord>(`/karren/${nr}/${karId}`),
  karHistorie: (nr: string, karId: string) =>
    verzoek<{ historie: Gebeurtenis[] }>(`/karren/${nr}/${karId}/historie`),

  // Materiaalstaat
  onderdeelBijwerken: (nr: string, id: string, wijziging: OnderdeelWijziging) =>
    patch<{ onderdeel: Onderdeel }>(`/onderdelen/${nr}/${id}`, wijziging),
  goedkeuren: (nr: string, id: string, sleutel: string, intrekken = false) =>
    post<{ goedkeuringen: Record<string, Handeling> }>(`/onderdelen/${nr}/${id}/goedkeuren`, {
      sleutel,
      intrekken,
    }),

  // Kar: splitsen, vrijgeven, on-hold. De kar is de eenheid die door de werkplaats
  // rijdt, dus deze handelingen horen daar.
  splitsen: (
    nr: string,
    karId: string,
    karren: Array<{ kar?: string; bouwelementIds: string[] }>,
  ) => post<{ karren: unknown[] }>(`/karren/${nr}/${karId}/splitsen`, { karren }),
  produceren: (nr: string, karId: string) => post<{ kar: Kar }>(`/karren/${nr}/${karId}/produceren`),
  onHold: (nr: string, karId: string, reden: string) =>
    post<{ kar: Kar }>(`/karren/${nr}/${karId}/on-hold`, { reden }),
  onHoldOpheffen: (nr: string, karId: string) =>
    verzoek<{ kar: Kar }>(`/karren/${nr}/${karId}/on-hold`, { method: 'DELETE' }),

  // Werkvloer
  werkvloer: (nr: string) => verzoek<Werkvloer>(`/werkvloer/${nr}`),
  afstrepen: (nr: string, karId: string, sleutel: string) =>
    post<{ kar: Kar; bronkarBijgewerkt: string | null }>(
      `/karren/${nr}/${karId}/stap/${sleutel}/afstrepen`,
    ),
  stapInstellen: (
    nr: string,
    karId: string,
    sleutel: string,
    invoer: {
      fotoVerplicht?: boolean;
      normZichtbaarVoorProductie?: boolean;
      normtijd?: Normtijd;
      opmerking?: string;
    },
  ) => patch<{ stap: Weergavestap }>(`/karren/${nr}/${karId}/stap/${sleutel}`, invoer),
  foto: (
    nr: string,
    karId: string,
    sleutel: string,
    invoer: { bestandsnaam: string; dataUrl: string; onderdeelNummer?: string },
  ) => post<{ foto: string }>(`/karren/${nr}/${karId}/stap/${sleutel}/foto`, invoer),
  rework: (
    nr: string,
    karId: string,
    invoer: {
      vanafSleutel: string;
      reden: string;
      aparteKar: boolean;
      /** Bij een aparte kar: de code van die kar binnen de ruimte, bv. `09R`. */
  karcode?: string;
      stuks?: Array<{ onderdeelId: string; aantal: number }>;
    },
  ) => post<{ kar: unknown; reworkKar?: unknown }>(`/karren/${nr}/${karId}/rework`, invoer),
  verplaatsen: (nr: string, karId: string, sleutel: string, naarIndex: number) =>
    post<{ kar: unknown }>(`/karren/${nr}/${karId}/route/verplaatsen`, { sleutel, naarIndex }),
  routeHerstellen: (nr: string, karId: string) =>
    post<{ kar: unknown }>(`/karren/${nr}/${karId}/route/herstellen`),

  // Dashboard en planning
  dashboard: (nr: string) => verzoek<Dashboard>(`/dashboard/${nr}`),
  planning: (nr: string) => verzoek<PlanningAntwoord>(`/planning/${nr}`),
  /** Een kar in de agenda zetten of verplaatsen; `start: null` haalt hem eruit. */
  planningKar: (nr: string, karId: string, start: string | null, eind: string | null) =>
    verzoek<{ kar: string; start: string | null; eind: string | null }>(`/planning/${nr}/${karId}`, {
      method: 'PUT',
      body: JSON.stringify({ start, eind }),
    }),

  // Bewerkingen en berekeningen
  bewerkingsregels: (nr: string) => verzoek<RegelsAntwoord>(`/bewerkingsregels/${nr}`),
  bewerkingsregelOpslaan: (nr: string, regel: Bewerkingsregel) =>
    verzoek<{ regel: Bewerkingsregel }>(`/bewerkingsregels/${nr}/${regel.id}`, {
      method: 'PUT',
      body: JSON.stringify(regel),
    }),
  bewerkingsregelVerwijderen: (nr: string, id: string) =>
    verzoek<{ verwijderd: string }>(`/bewerkingsregels/${nr}/${id}`, { method: 'DELETE' }),
  /**
   * De regelset uitvoeren op één echt onderdeel. Met `regels` erbij test je een nog
   * niet opgeslagen wijziging — zo is te zien wat een aanpassing doet vóór het opslaan.
   */
  regelsTesten: (nr: string, onderdeelId?: string, regels?: Bewerkingsregel[]) =>
    post<RegeltestAntwoord>(`/bewerkingsregels/${nr}/test`, { onderdeelId, regels }),
  /** De routes van bestaande karren in lijn brengen met de regels. */
  routesBijwerken: (nr: string) =>
    post<{ bijgewerkt: number; afwijkend: string[] }>(`/bewerkingsregels/${nr}/routes-bijwerken`),

  // 3D-model
  model: (nr: string, bouwelementId: string) =>
    verzoek<ModelAntwoord>(`/model/${nr}/${bouwelementId}`),
  modelUploaden: (
    nr: string,
    bouwelementId: string,
    invoer: { bestandsnaam: string; dataUrl: string; onderdeelId?: string; revisie?: string },
  ) => post<{ stl: StlBestand }>(`/model/${nr}/${bouwelementId}`, invoer),
  /**
   * De geüploade STL-export. Eigen pad, geen `model/{nr}/bestand`: dat botst met
   * `model/{nr}/{bouwelementId}` en werd door de Functions-host daarheen gerouteerd.
   */
  modelBestandUrl: (nr: string, verwijzing: string) =>
    `/api/modelbestand/${nr}?verwijzing=${encodeURIComponent(verwijzing)}`,
  /**
   * Het voorbeeldmodel uit de maten, voor zolang er nog geen TopSolid-export is.
   * Elk onderdeel is daar een balk — het is geen tekening.
   */
  modelVoorbeeldUrl: (
    nr: string,
    bouwelementId: string,
    keuze?: { karId?: string; onderdeelId?: string },
  ) => {
    const query = new URLSearchParams();
    if (keuze?.karId) query.set('kar', keuze.karId);
    if (keuze?.onderdeelId) query.set('onderdeel', keuze.onderdeelId);
    const staart = query.toString();
    return `/api/model/${nr}/${bouwelementId}/voorbeeld.stl${staart ? `?${staart}` : ''}`;
  },

  // Beheer
  coderingen: (nr: string) => verzoek<Coderingen>(`/coderingen/${nr}`),
  coderingenOpslaan: (nr: string, coderingen: Coderingen) =>
    verzoek<Coderingen>(`/coderingen/${nr}`, { method: 'PUT', body: JSON.stringify(coderingen) }),
  plaatmateriaal: () =>
    verzoek<{
      plaatmateriaal: PlaatMateriaal[];
      machines: Machine[];
      conversie: ConversieRegel[];
      dagcapaciteit: Record<string, number>;
    }>('/plaatmateriaal'),
  /**
   * De bouwnummeroverstijgende lijsten opslaan. De dagcapaciteit valt hierbinnen onder
   * het admin-recht `normtijdenBewerken`; de API weigert het anders.
   */
  algemeenOpslaan: (lijsten: { dagcapaciteit?: Record<string, number> }) =>
    verzoek<{
      plaatmateriaal: PlaatMateriaal[];
      machines: Machine[];
      conversie: ConversieRegel[];
      dagcapaciteit: Record<string, number>;
    }>('/plaatmateriaal', { method: 'PUT', body: JSON.stringify(lijsten) }),
  audit: (nr: string) => verzoek<AuditRegel[]>(`/audit/${nr}`),

  // Import en export
  importeren: (nr: string, onderdelen: Onderdeel[], bron: string) =>
    post<{ geimporteerd: number; karren: number; bouwelementen: number }>(`/houtstaat/${nr}/import`, {
      onderdelen,
      bron,
    }),
  archiveren: (nr: string) => post<{ gearchiveerd: boolean; pad?: string }>(`/export/${nr}/archiveren`),
  exportUrl: (nr: string, formaat: 'html' | 'csv', bouwelementId?: string) =>
    `/api/export/${nr}?formaat=${formaat}${bouwelementId ? `&bouwelement=${encodeURIComponent(bouwelementId)}` : ''}`,

  baanbreedte: (paneelBreedte: number, maxBaanBreedte: number) =>
    post<{ aantalBanen: number; baanBreedte: number; naden: number[] }>('/fineer/baanbreedte', {
      paneelBreedte,
      maxBaanBreedte,
    }),
};
